import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BadgeDollarSign, BookOpen, Check, ExternalLink, Facebook, Info, Instagram, KeyRound, Linkedin, Loader2, Mail, Music2, Plug, RefreshCw, ShieldCheck, Twitter, Unplug, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime, humanize } from "@/lib/api";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { Chip, PageHeader, QueryState } from "@/components/common";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ConnectedAccountsSummary } from "@/components/ConnectedAccountsSummary";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ICONS = { facebook_pages: Facebook, instagram_business: Instagram, meta_ads: BadgeDollarSign, linkedin_pages: Linkedin, linkedin_ads: Linkedin,
  x: Twitter, google_ads: BadgeDollarSign, youtube: Youtube, tiktok: Music2, email_provider: Mail };
const STATUS = {
  connected: ["green", "Connected"], awaiting_selection: ["amber", "Choose accounts"], attention: ["amber", "Missing permissions"],
  expired: ["red", "Authorization expired"], revoked: ["red", "Authorization revoked"], error: ["red", "Connection error"],
  not_connected: ["slate", "Not connected"], configuration_required: ["amber", "Configuration required"], planned: ["slate", "Adapter not built yet"],
};
const ERRORS = {
  invalid_state: "That authorization link expired or was already used. Start the connection again.",
  session_mismatch: "Finish the connection in the same signed-in browser you started it from.",
  forbidden: "Your role can't connect channels in this workspace.", declined: "Authorization was cancelled. Nothing was connected.",
  exchange_failed: "The platform didn't accept the authorization. Please try again.", token_invalid: "The platform reported this authorization as invalid.",
  discovery_failed: "Authorization worked, but account discovery failed. Check the granted permissions and try again.",
  provider_unavailable: "The platform is unavailable right now. Try again shortly.", invalid_request: "That connection request was invalid.",
};

function ConnectDialog({ family, channels, open, onOpenChange }) {
  const { path } = useWorkspace();
  const [keys, setKeys] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setKeys(channels.map((c) => c.key)); }, [open, channels]);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(path(`/channels/${family}/connect`), { channel_keys: keys }); window.location.assign(data.authorize_url); }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="channel-connect-dialog" className="bg-[#0A1024] border-[#243153] text-[#F7FAFF] max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Connect {channels[0]?.family_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#AAB6CC]">Choose what CRESQEN may read. Only the permissions those channels need are requested. You'll sign in on the platform's own page; CRESQEN never sees your password.</p>
        <div className="space-y-2">{channels.map((c) => (
          <label key={c.key} className="flex items-center gap-3 p-3 rounded-lg border border-[#172342] cursor-pointer">
            <input type="checkbox" data-testid={`connect-channel-${c.key}`} checked={keys.includes(c.key)} onChange={() => setKeys((k) => (k.includes(c.key) ? k.filter((x) => x !== c.key) : [...k, c.key]))} className="accent-[#22E6F2]" />
            <span className="text-sm">{c.name}</span>
          </label>))}</div>
        <p className="text-xs text-[#7C8BAD]">Read-only: account identity, pages and ad accounts. Publishing, ad changes and budget changes are disabled.</p>
        <div className="flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} className="cq-btn cq-btn-ghost">Cancel</button>
          <button data-testid="channel-connect-continue" disabled={busy || !keys.length} onClick={go} className="cq-btn cq-btn-primary">{busy ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />} Continue to {channels[0]?.family_name}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssetPicker({ c }) {
  const { path, refresh, can } = useWorkspace();
  const [sel, setSel] = useState(c.selected_asset_ids);
  const [busy, setBusy] = useState(false);
  useEffect(() => setSel(c.selected_asset_ids), [c.selected_asset_ids]);
  if (!c.available_assets.length) return <p className="text-xs text-[#7C8BAD]">No accounts were found for this authorization.</p>;
  const dirty = JSON.stringify([...sel].sort()) !== JSON.stringify([...c.selected_asset_ids].sort());
  const save = async () => {
    setBusy(true);
    try { await api.put(path(`/channels/${c.key}/selection`), { asset_ids: sel }); toast.success("Accounts assigned to this workspace"); refresh(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`channel-assets-${c.key}`}>
      <div className="cq-muted-label mb-1.5">Available accounts · choose which belong to this workspace</div>
      <div className="space-y-1.5">{c.available_assets.map((a) => (
        <label key={a.id} className="flex items-center gap-2.5 text-sm text-[#D5DDEB]">
          <input type="checkbox" data-testid={`channel-asset-${c.key}-${a.id}`} disabled={!can("manage_channels")} checked={sel.includes(a.id)}
            onChange={() => setSel((s) => (s.includes(a.id) ? s.filter((x) => x !== a.id) : [...s, a.id]))} className="accent-[#22E6F2]" />
          <span className="truncate">{a.name}</span>{a.detail && <span className="text-[11px] text-[#7C8BAD] truncate">{a.detail}</span>}
        </label>))}</div>
      {dirty && <button data-testid={`channel-assets-save-${c.key}`} disabled={busy} onClick={save} className="mt-2 cq-btn cq-btn-primary !py-1 !text-xs">{busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save selection</button>}
    </div>
  );
}

function Row({ label, children }) {
  return <div className="flex gap-3 text-xs"><span className="w-32 shrink-0 text-[#7C8BAD]">{label}</span><span className="text-[#D5DDEB] min-w-0 break-words">{children}</span></div>;
}

function ChannelCard({ c }) {
  const Icon = ICONS[c.key] || Plug;
  const [tone, label] = STATUS[c.status] || ["slate", humanize(c.status)];
  const linked = !["not_connected", "configuration_required", "planned"].includes(c.status);
  return (
    <div data-testid={`channel-card-${c.key}`} className="cq-card p-5 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#050816] border border-[#243153] flex items-center justify-center shrink-0"><Icon size={18} className="text-[#22E6F2]" /></span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[15px] text-[#F7FAFF]">{c.name}</div>
          <div className="mt-1 flex flex-wrap gap-1.5"><span data-testid={`channel-status-${c.key}`}><Chip tone={tone}>{label}</Chip></span>
            {c.capabilities.map((cap) => <span key={cap.key} title={cap.note || "Enabled: read-only"} className={`cq-chip ${cap.enabled ? "text-[#22E6F2] border-[#22E6F2]/30" : "text-[#5D6B8C] border-[#243153] line-through"}`}>{humanize(cap.key)}</span>)}
          </div>
        </div>
      </div>
      {c.status === "configuration_required" && <p className="text-xs text-[#F4B860]" data-testid={`channel-config-${c.key}`}><KeyRound size={12} className="inline mr-1" />{c.config_note ? `Configuration required: ${c.config_note}.` : `Needs the ${c.family_name} app credentials before anyone can connect.`}</p>}
      {linked && !c.configured && <p className="text-xs text-[#F4B860]"><KeyRound size={12} className="inline mr-1" />App credentials are missing, so this connection can't be checked or reconnected.</p>}
      {c.status === "planned" && <p className="text-xs text-[#7C8BAD]">No adapter is built for this platform yet, so it can't be connected and no data is shown. Use exports and CSV imports meanwhile.</p>}
      {linked && (
        <div className="space-y-1.5">
          <Row label="Account">{c.identity?.name || "Unknown"}</Row>
          <Row label="Health"><span className={c.health === "healthy" ? "text-[#4BE3A4]" : "text-[#F4B860]"}>{humanize(c.health)}</span></Row>
          <Row label="Authorization">{humanize(c.token_status)}{c.token_expires_at ? ` · expires ${fmtDateTime(new Date(c.token_expires_at * 1000).toISOString())}` : ""}</Row>
          <Row label="Last verified">{c.last_sync_at ? fmtDateTime(c.last_sync_at) : "Never"}</Row>
          <Row label="Permissions">{c.granted_permissions.join(", ") || "None"}</Row>
          {c.missing_permissions.length > 0 && <Row label="Missing"><span className="text-[#FF7792]">{c.missing_permissions.join(", ")} · reconnect and allow these</span></Row>}
          {c.last_error && <Row label="Last error"><span className="text-[#FF7792]">{c.last_error}</span></Row>}
        </div>)}
      {linked && c.token_status === "valid" && <AssetPicker c={c} />}
    </div>
  );
}

function FamilyBlock({ family, channels }) {
  const { path, refresh, can } = useWorkspace();
  const [connect, setConnect] = useState(false);
  const [disc, setDisc] = useState(false);
  const [busy, setBusy] = useState(false);
  const first = channels[0];
  const linked = channels.some((c) => !["not_connected", "configuration_required", "planned"].includes(c.status));
  const sync = async () => {
    setBusy(true);
    try { await api.post(path(`/channels/${family}/sync`)); toast.success("Authorization verified and accounts refreshed"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); refresh(); }
  };
  const disconnect = async () => {
    try { await api.post(path(`/channels/${family}/disconnect`), { confirm: true }); toast.success(`${first.family_name} disconnected`); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const manage = can("manage_channels");
  return (
    <section data-testid={`channel-family-${family}`} className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-lg text-[#F7FAFF]">{first.family_name}</h2>
        <span className="font-mono text-[10px] text-[#7C8BAD]">{first.category?.toUpperCase()} · {first.live_connector ? "READ-ONLY ADAPTER" : "NO ADAPTER YET"}</span>
        <span className="flex-1" />
        {first.live_connector && first.configured && manage && (<>
          {linked && <button data-testid={`channel-sync-${family}`} disabled={busy} onClick={sync} className="cq-btn cq-btn-ghost !py-1.5 !text-xs">{busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Check connection</button>}
          <button data-testid={`channel-connect-${family}`} onClick={() => setConnect(true)} className="cq-btn cq-btn-primary !py-1.5 !text-xs"><Plug size={13} /> {linked ? "Reconnect" : "Connect"}</button>
        </>)}
        {linked && manage && <button data-testid={`channel-disconnect-${family}`} onClick={() => setDisc(true)} className="cq-btn cq-btn-danger !py-1.5 !text-xs"><Unplug size={13} /> Disconnect</button>}
        {first.live_connector && !first.configured && manage && (<>
          <Link to={`/channels/setup#${family}`} data-testid={`channel-resume-setup-${family}`} className="cq-btn cq-btn-primary !py-1.5 !text-xs"><KeyRound size={13} /> Resume setup</Link>
          <Link to={`/channels/setup#${family}`} data-testid={`channel-open-guide-${family}`} className="cq-btn cq-btn-ghost !py-1.5 !text-xs"><BookOpen size={13} /> Open setup guide</Link>
          <Link to={`/channels/setup#${family}-credentials`} data-testid={`channel-view-credentials-${family}`} className="cq-btn cq-btn-ghost !py-1.5 !text-xs">View required credentials</Link>
          <Link to={`/channels/setup#${family}-permissions`} data-testid={`channel-view-permissions-${family}`} className="cq-btn cq-btn-ghost !py-1.5 !text-xs">View required permissions</Link>
        </>)}
        {!manage && first.live_connector && <span className="text-xs text-[#7C8BAD]" data-testid={`channel-no-permission-${family}`}>Only owners and admins can connect channels</span>}
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{channels.map((c) => <ChannelCard key={c.key} c={c} />)}</div>
      {first.live_connector && <ConnectDialog family={family} channels={channels} open={connect} onOpenChange={setConnect} />}
      <ConfirmDialog open={disc} onOpenChange={setDisc} danger title={`Disconnect ${first.family_name}?`} confirmLabel="Disconnect"
        description="CRESQEN deletes its stored authorization, asks the platform to revoke access, and removes the discovered accounts from this workspace. This is recorded in the activity log." onConfirm={disconnect} />
    </section>
  );
}

export default function Channels() {
  const q = useWsQuery("/channels");
  const { can } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const shown = useRef(null);
  useEffect(() => {
    const ok = params.get("connected"); const err = params.get("error");
    const sig = `${ok}|${err}`;
    if ((ok || err) && shown.current !== sig) {
      shown.current = sig;
      if (ok) toast.success("Authorization tested successfully. Choose which accounts belong to this workspace.");
      if (err) toast.error(ERRORS[err] || "The connection didn't complete. Please try again.");
    }
    if (ok || err) setParams({}, { replace: true });
  }, [params, setParams]);
  const groups = useMemo(() => {
    const g = {};
    (q.data || []).forEach((c) => { (g[c.family] = g[c.family] || []).push(c); });
    return Object.entries(g).sort(([, a], [, b]) => a[0].phase - b[0].phase);
  }, [q.data]);
  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Measure" title="Channel Connection Centre" description="Connect the platforms CRESQEN may read from. Each connection belongs to this workspace only." />
      <div className="flex gap-3 p-4 rounded-xl border border-[#22E6F2]/25 bg-[#22E6F2]/5 text-sm text-[#D5DDEB]" data-testid="channels-notice">
        <ShieldCheck size={16} className="text-[#4BE3A4] shrink-0 mt-0.5" />
        <span>Connections are read-only. CRESQEN never publishes content, launches ads or changes budgets. Authorizations are stored encrypted on the server and are never sent to your browser. A channel shows as connected only after its authorization has been tested.</span>
      </div>
      <QueryState q={q}>
        {(data) => (<>
          <ConnectedAccountsSummary channels={data} canManage={can("manage_channels")} />
          {groups.map(([fam, chans]) => <FamilyBlock key={fam} family={fam} channels={chans} />)}
        </>)}
      </QueryState>
      <p className="text-xs text-[#7C8BAD] flex gap-2"><Info size={13} className="shrink-0 mt-0.5" /> Platforms without an adapter have no Connect button. Until a connector is live, publish manually from Content, export campaigns, and import platform CSV reports in Analytics.</p>
    </div>
  );
}
